// AI suggestion model — the unit a human reviews per inbound turn.
//
// The AI never acts on its own. For each inbound turn it produces ONE suggestion
// package: a classified intent, a drafted reply, zero or more PROPOSED actions
// (queued, never executed), and a QA grade. A human then Approves / Escalates /
// Edits. Only Approve sends a reply or executes an action (execution wired later).
//
// These zod schemas are the source of truth; the Postgres schema in
// server/migrations/0003_ai_suggestions.sql mirrors them. Freshly designed for
// CXHubV2 (logic informed by the old platform, code not ported).

import { z } from 'zod'
import { RMA_REFERENCE_TOKEN } from './rmartn-cust-pays.js'

// Coarse intent taxonomy for the cheap first-pass classifier. Kept small on
// purpose; we expand it as real eBay/email traffic shows what actually arrives.
export const Intent = z.enum([
  'order_status',                // "where is my order / tracking?"
  'cancellation',                // explicit, unconditional request to cancel
  'return_refund',               // return, refund, or exchange
  'return_status',               // customer asking if their returned item has been received yet
  'replacement',                 // the part we sent is wrong or missing pieces (wrong_item / missing_parts) and the customer wants it put right or doesn't say; lost-package replacements too (2026-10-29 split)
  'resend_return_label',         // customer needs a return shipping label resent or doesn't know how to get one
  'address_confirmation_reply',  // buyer replies to our address-verification outreach (confirm or new address)
  'fitment_confirmation',        // LEGACY (2026-10-01): split into the two below; kept so existing contacts still display. The classifier never returns it.
  'fitment_preorder',            // will this part fit / which part fits — asked BEFORE buying
  'fitment_postorder',           // a fitment question or problem with a part ALREADY bought/received/installed
  'product_question',            // general product question (specs, warranty, dimensions, etc.)
  'product_availability',        // is this part in stock / do you carry it?
  'pricing',                     // asking about the price of a part or order
  'shipping_issue',              // the package and its trip (lost, late, stuck, delivered-not-found, wrong address, carrier) + a part that arrived damaged; never a wrong part or missing pieces (2026-10-29 split)
  'product_defect',              // part failed / stopped working after functioning normally for a while
  'billing',                     // charge / invoice / payment questions on an existing order
  'invoice_request',             // customer asks for a copy of their invoice/receipt on an existing order
  'sales_hold',                  // deterministic: a "Sales Hold Applied to <order>" ops email (see pipeline.js's isSalesHoldEmail())
  'sales_hold_phone_call',       // deterministic: a reply to the internal "Call Customer to Resolve Sales Hold" ops email (see sales-hold-phone-call.js's isSalesHoldPhoneCallEmail())
  'negative_feedback',           // complaint / negative sentiment with no actionable request -- stays open for a human, never automated
  'data_deletion_request',       // deterministic: a Website Contact Us submission requesting data deletion (see channels/m365/data-deletion-request.js)
  // 1A_SUPPORT_EMAIL only (2026-10-06; server/src/channels/m365/support-intent.js).
  // Never in the global classifier's list (prompts.js); classify-only for now.
  'employee_order',              // deterministic: the 'Here is the shopping cart you requested' email
  'bbb_review',                  // deterministic: a BBB Customer Review email
  'gp_assistance',               // deterministic: an RMA Removal/Delete request
  'call_back_request',           // deterministic: '<someone> has requested a call back'
  'dropship_vendor_reply',       // a sender outside our own domains (a dropship vendor or supplier)
  'internal_email',              // anything else on that mailbox
  // Task contacts only (2026-10-14; docs/decisions/2026-10-14-task-flows-reset.md).
  // Chosen by the task's trigger, never by any classifier.
  'confirm_refund_value',        // a task due on 'return_document_created'
  'update_replacement_tracking', // a task due on 'replacement_tracking_available', eBay order
  'no_action_required',          // closing message; conversation already resolved
  'other',                       // anything the classifier can't confidently bucket
])

// The intents only a task contact's trigger picks (2026-10-14): never offered
// to the customer classifier (server/src/ai/prompts.js lists neither).
export const TASK_ONLY_INTENTS = Object.freeze(['confirm_refund_value', 'update_replacement_tracking'])

// Display labels for the Intent enum, shared between the server (feedback
// messages, logs) and the UI (ui/src/constants/intents.js's INTENT_OPTIONS) —
// single source of truth so the two can never drift apart on wording.
export const INTENT_LABELS = {
  order_status:         'Order Status',
  cancellation:         'Cancellation',
  return_refund:        'Return / Refund',
  return_status:        'Return Status',
  replacement:          'Replacement',
  resend_return_label:  'Resend Return Label',
  address_confirmation_reply: 'Address Confirmation Reply',
  fitment_confirmation: 'Fitment / Compatibility',
  fitment_preorder:     'PreOrder Fitment / Compatibility',
  fitment_postorder:    'Post-Order Fitment / Compatibility',
  product_question:     'Product Question',
  product_availability: 'Product Availability',
  pricing:              'Pricing',
  shipping_issue:       'Shipping Issue',        // the package and its trip, and damaged parts (2026-10-29); a wrong part / missing pieces is Replacement
  product_defect:       'Product Defect',
  billing:              'Billing',
  invoice_request:      'Invoice Request',
  sales_hold:           'Sales Hold',
  sales_hold_phone_call: 'Sales Hold Phone Call',
  negative_feedback:    'Negative Feedback',
  data_deletion_request: 'Data Deletion Request',
  employee_order:       'Employee Order',
  bbb_review:           'BBB Review',
  gp_assistance:        'GP Assistance',
  call_back_request:    'Call Back Request',
  dropship_vendor_reply: 'Dropship/Vendor Reply',
  internal_email:       'Internal Email',
  confirm_refund_value: 'Confirm Refund Value',
  update_replacement_tracking: 'Update Replacement Tracking',
  no_action_required:   'No Action Required',
  other:                'Other',
  // Rule-engine outcome, not in the Zod enum, but still labeled for display.
  // "Spam / Informational" because on eBay this is overwhelmingly platform
  // notification traffic we don't action -- not abusive junk. The stored value
  // stays 'spam' (SQL, rules, prompts all key on it); only the wording moved.
  // Worth revisiting when email lands and real spam exists alongside this.
  spam:                 'Spam / Informational',
}

export function intentLabel(value) {
  return INTENT_LABELS[value] ?? value
}

// Priority levels for the open Queue's default (Oldest First) sort live in
// the database since 2026-10-01 (intent_priorities, migration 0093, edited
// by admins in Settings → Prioritization): levels 0-3, lower sorts first.
// The server's ORDER BY reads the table directly (repositories/
// conversations.js's intentPrioritySql()); the UI's client-side re-sort
// (conversationListMerge.js) uses the same map, fetched from
// GET /api/intent-priorities. An intent with no row is this level — the one
// value both sides need without a lookup.
export const DEFAULT_INTENT_PRIORITY_TIER = 3

// Per-message customer-frustration signal (feature/frustration-capture,
// 2026-09-26) — captured from EVERY inbound message's classification,
// admin-visible only (see ui/src/ConversationActivity.jsx and routes/ai.js's
// own admin-only strip of this field for every other role). Capture only:
// no Flows/automation gating or reporting reads this yet. An anchored 0-3
// scale plus independent trigger flags — see prompts.js's CLASSIFY_SYSTEM
// for the full anchored definitions given to the model.
export const FRUSTRATION_FLAGS = [
  'chargeback_or_dispute',
  'bad_review_or_feedback',
  'legal_or_regulator',
  'repeat_contact_unresolved',
  'wants_supervisor',
]
export const FrustrationFlag = z.enum(FRUSTRATION_FLAGS)

// 0 calm, 1 concerned, 2 frustrated, 3 angry — shared between the server
// (modelMeta.frustration.label) and the UI chip, so the two can never
// disagree on wording, same purpose as INTENT_LABELS above.
export const FRUSTRATION_LABELS = {
  0: 'Calm',
  1: 'Concerned',
  2: 'Frustrated',
  3: 'Angry',
}

// The classifier's raw per-message output — server/src/ai/classify.js
// validates the model's own JSON against this before ever using it. Strict:
// an unknown flag or an out-of-range level fails the WHOLE block (never
// partially salvaged), and classify.js stores null rather than defaulting to
// level 0 — 0 is a real, scored "calm" result, not "not scored."
// A customer's own request, anywhere in a replacement conversation, to ship the
// replacement to a different address or faster (2026-10-02; server/src/ai/
// replacement-shipping-request.js). Extracted from the REDACTED customer
// messages, then restored to real values on the server. The latest stance
// wins: a request the customer later withdrew is false. address holds only the
// fields the customer actually gave; the quotes are the customer's own words.
const optionalText = z.string().trim().min(1).optional()
export const ReplacementShippingAddress = z.object({
  name: optionalText,
  address1: optionalText,
  address2: optionalText,
  city: optionalText,
  region: optionalText,
  zip: optionalText,
  country: optionalText,
})
export const ReplacementShippingRequest = z.object({
  addressRequested: z.boolean(),
  address: ReplacementShippingAddress.nullable(),
  addressQuote: z.string().nullable(),
  expeditedRequested: z.boolean(),
  expeditedQuote: z.string().nullable(),
})
export const NO_REPLACEMENT_SHIPPING_REQUEST = Object.freeze({ addressRequested: false, address: null, addressQuote: null, expeditedRequested: false, expeditedQuote: null })

export const Frustration = z.object({
  level: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
  flags: z.array(FrustrationFlag),
  evidence: z.string(),
})

// address_confirmation_reply-specific entities the classifier extracts from
// the buyer's actual reply text. Entities as a whole are a loose passthrough
// object (classify.js validates none of this per-field — see its own
// comment), but this documents the shape as the source of truth, same
// purpose as the other schemas in this file. correctedAddress fields are
// each optional since the buyer may correct only one field (e.g. just the
// street) and leave the rest unstated.
export const CorrectedAddress = z.object({
  shippingName: z.string().optional(),
  shippingAddress1: z.string().optional(),
  shippingAddress2: z.string().optional(),
  shippingCity: z.string().optional(),
  shippingRegion: z.string().optional(),
  shippingPostalCode: z.string().optional(),
})

export const AddressConfirmationEntities = z.object({
  // true when the buyer confirmed the existing address with no changes.
  addressConfirmed: z.boolean().optional(),
  // Populated only when the buyer actually provided a correction.
  correctedAddress: CorrectedAddress.nullable().optional(),
})

// The full description of a single API call the AI intends to make.
// Surfaced in the UI via "View Call" so a CSR can inspect before approving.
export const ApiCall = z.object({
  service: z.string(),                      // e.g. "TRQ", "eBay"
  method: z.string(),                       // "GET", "POST", "PATCH", etc.
  url: z.string(),                          // full URL with resolved path params where possible
  body: z.record(z.unknown()).optional(),   // request body (omitted for GET/DELETE)
})

// "Template used" (docs/decisions.md, 2026-10-01): which template a draft, a
// proposed step, or a sent reply came from, for the Activity Log. Folder and
// title only — no customer data.
//   source 'ai_selected'    the drafter picked it from its candidate templates
//          'fixed'          a deterministic resolveTemplate() path chose it
//          'agent_inserted' a CSR inserted it with the Templates button
// Lives at modelMeta.draftTemplate (the AI draft), params.template (a proposed
// step) and ai_suggestions.sent_template (the reply actually sent).
export const TEMPLATE_SOURCES = ['ai_selected', 'fixed', 'agent_inserted']
export const TemplateUsed = z.object({
  folder: z.string().nullable(),
  title: z.string(),
  source: z.enum(TEMPLATE_SOURCES),
})

// The TemplateUsed for a resolved template row ({ folder, title, ... }), or
// null when there is none.
export function templateUsed(template, source) {
  if (!template?.title) return null
  return { folder: template.folder ?? null, title: template.title, source }
}

// Parses an untrusted value (e.g. a request body field) into a TemplateUsed,
// or null when it isn't one — never throws.
export function parseTemplateUsed(value) {
  const r = TemplateUsed.safeParse(value)
  return r.success ? r.data : null
}

// The TemplateUsed a sent reply came from (ai_suggestions.sent_template):
// the template a CSR inserted, when they inserted one; otherwise the AI
// draft's own template (modelMeta.draftTemplate) when the reply started from
// that draft; otherwise null. Only a decision that sends gets one.
export function sentTemplateFor({ status, insertedTemplate = null, draftTemplate = null, draftReply = '' }) {
  if (status !== 'approved' && status !== 'edited') return null
  const inserted = parseTemplateUsed(insertedTemplate ? { ...insertedTemplate, source: 'agent_inserted' } : null)
  if (inserted) return inserted
  if (!draftReply) return null
  return parseTemplateUsed(draftTemplate)
}

// One conditional path in a branching step. The AI doesn't know at generation
// time which branch will execute — it surfaces all paths so the CSR sees them.
export const ActionBranch = z.object({
  pathLabel: z.string(),  // e.g. "If return found", "If no eBay return exists"
  apiCall: ApiCall,
})

// A single proposed back-office action. STRUCTURED and SEPARATE from the draft
// text because it has a different review path ("execute this in TRQ" vs "send
// these words"). Always requires human approval; the AI only proposes.
//
// Each step represents exactly ONE external API call. If a call depends on a
// prior step's response, use `branches` (one per possible path) instead of a
// single `apiCall`. Never group multiple API calls into one step.
export const ProposedAction = z.object({
  // Stable action key from the catalog (server/src/ai/actions.js).
  type: z.string(),
  // Human-readable one-liner shown in the review queue,
  // e.g. "Cancel order ORDER123 in TRQ".
  label: z.string(),
  // The back-office system this action would hit (illustrative until the real
  // integrations land), e.g. "TRQ".
  targetSystem: z.string().nullable(),
  // The parameters that WOULD be sent (e.g. { orderId: "ORDER123" }). Never
  // sent automatically — surfaced so the human can verify before approving.
  params: z.record(z.unknown()).default({}),
  // Why the AI proposed it (ties back to the classified intent/entities).
  rationale: z.string().nullable().optional(),
  // Direct call — present when the full call is known at generation time.
  // Mutually exclusive with `branches`.
  apiCall: ApiCall.optional(),
  // Conditional paths — present when the URL/method/body depends on a prior
  // step's response. Each branch shows the call that would be made under that
  // condition. Mutually exclusive with `apiCall`.
  branches: z.array(ActionBranch).optional(),
})

// One QA dimension's score. Mirrors the four dimensions the old platform's
// grader used (carried forward per project memory): accuracy/relevance,
// tone/sentiment, compliance, consistency.
export const QaDimension = z.object({
  score: z.number().int().min(1).max(5),
  notes: z.string(),
})

export const QaGrade = z.object({
  accuracyRelevance: QaDimension,
  toneSentiment: QaDimension,
  compliance: QaDimension,
  consistency: QaDimension,
  // Rolled-up recommendation for the reviewer. Not an auto-gate — advisory.
  overall: z.object({
    // pass = looks send-ready; revise = needs an edit; block = do not send as-is.
    recommendation: z.enum(['pass', 'revise', 'block']),
    score: z.number().int().min(1).max(5),
    summary: z.string(),
  }),
})

// Lifecycle of a suggestion. The AI creates it 'pending'; the human moves it.
export const SuggestionStatus = z.enum([
  'pending', // awaiting human review
  'approved', // human approved (reply queued to send / actions to run)
  'edited', // human edited then approved — humanReply holds the final text
  'escalated', // handed to a person/team; draft not sent
  // Transfer to Support (2026-10-08): moved to the Support team with a note,
  // like 'escalated' still actionable; the conversation's status is unchanged.
  'transferred',
  'dismissed', // discarded (e.g. spam, no reply needed)
])

export const AiSuggestion = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  // The inbound message that triggered this suggestion (the latest turn).
  triggerMessageId: z.string().uuid().nullable(),
  status: SuggestionStatus.default('pending'),

  // 'spam' is set by the spam-rules path / the Triage Spam button, never by
  // the classifier — hence a union rather than widening the Intent enum the
  // classifier output is validated against.
  intent: z.union([Intent, z.literal('spam')]),
  intentConfidence: z.number().min(0).max(1),
  intentReasoning: z.string().nullable(),

  draftReply: z.string(),
  needsHumanReview: z.boolean().default(false),
  humanReviewReason: z.string().nullable().optional(),
  proposedActions: z.array(ProposedAction).default([]),
  qaGrade: QaGrade.nullable(),

  // Non-PII bookkeeping: models used, redaction counts, token usage, timings.
  // Never contains customer content.
  modelMeta: z.record(z.unknown()).default({}),

  // Set when a human edits before approving (the text actually used).
  humanReply: z.string().nullable().optional(),
  // The exact, fully-resolved text actually transmitted to eBay once an
  // Approve/Edit send succeeds — distinct from humanReply, which is the
  // CSR-approved text before send-time placeholder resolution.
  sentReply: z.string().nullable().optional(),
  // Which template the sent reply came from (migration 0092): the CSR's
  // inserted template, else the AI draft's own when the reply started from it.
  sentTemplate: TemplateUsed.nullable().optional(),

  // Grounding references captured at generation time (migrations 0004/0005/
  // 0007/0014): the TRQ order, decoded VIN, eBay case, and INR inquiry this
  // suggestion was grounded against — surfaced in the review panel.
  erpOrderId: z.string().nullable().optional(),
  vin: z.string().nullable().optional(),
  caseId: z.string().nullable().optional(),
  inrRequestId: z.string().nullable().optional(),

  // Flows automation (migrations 0019/0017/0020): the computed flow key this
  // suggestion matched (null = no confident flow), the pipeline's own
  // disposition guess + confidence, and the human-recorded disposition.
  flowKey: z.string().nullable().optional(),
  aiDisposition: z.string().nullable().optional(),
  aiDispositionConfidence: z.number().min(0).max(1).default(0),
  disposition: z.string().nullable().optional(),

  // English overlay of the draft for a non-English conversation (migration 0008).
  translationEn: z.string().nullable().optional(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  decidedAt: z.coerce.date().nullable(),
  decidedBy: z.string().nullable(), // reviewer's Entra username
})

// --- Send-button visibility / "Process counts as Send" (2026-09-25) --------
// Whether a suggestion has no "AI Drafted Reply" box of its own in the UI —
// shared by both sides, since both need the SAME answer: the UI (which box
// to render, whether the Send button should show at all — SuggestionPanel.jsx's
// own render gate for both) and the server (whether Processing a customer-
// facing proposed step should itself count as the Send — routes/ai.js's
// per-step Process route). The three conditions are exactly the three cases
// SuggestionPanel.jsx's own drafted-reply box gate already enumerates:
//   - FROM_EBAY (an eBay System Message notification — no free-text reply of
//     our own to draft, only proposed actions);
//   - automated_return_notice_default (a fully deterministic reply, no
//     drafting involved);
//   - an eBay-client m365_email conversation (channel/client combination
//     where the reply is composed as an eBay case message, not an email —
//     no email-style draft box).
// Moved here from ui/src/suggestionActionInputs.js (formerly UI-only) so the
// server can compute the identical answer without duplicating the rule —
// see docs/decisions.md (2026-09-25) for the incident this closes.
export function hasNoDraftBoxOfItsOwn({ fromEbay, intent, isEbayClientEmail }) {
  return Boolean(fromEbay) || intent === 'automated_return_notice_default' || Boolean(isEbayClientEmail)
}

// --- Signature-only reply == empty reply (2026-09-28) -----------------------
// A blank draft box now starts pre-filled with the conversation's resolved
// signature template (channels/m365/signature.js's resolveSignatureTemplate)
// instead of literally nothing — so "did the CSR actually write anything"
// can no longer be answered by draft.trim() alone; a box holding only the
// seeded signature must count as empty everywhere a truly blank box always
// did: the Send button's enabled/visible state, "Process counts as Send",
// send-approved-reply.js's own send-time refusal, and Flows automation/the
// automation-due sweep (an automated run must never send just a signature).
// Shared so server and UI can't drift on what "just the signature" means.
//
// Compares after stripping HTML tags and collapsing whitespace only — NOT
// after resolving [AGENT_FIRST_NAME] itself, since a real name substituted
// into `text` (the box always resolves the token as soon as it seeds — see
// SuggestionPanel.jsx's resolveAgentFirstNamePlaceholder) has no fixed
// relationship to the literal token text alone; stripping the token out of
// only one operand would leave the OTHER operand's real name as unmatched
// leftover content. Callers resolve the placeholder in `signatureTemplate`
// the same way (and with the same name) before calling, exactly like every
// pre-existing bare-footer comparison in this codebase already does — an
// automated caller with no known agent name simply leaves both operands'
// tokens literal, which already compares equal with no resolution at all.
export function normalizeForSignatureCompare(value) {
  return String(value ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function isSignatureOnlyReply(text, signatureTemplate) {
  const normalizedText = normalizeForSignatureCompare(text)
  if (!normalizedText) return true
  if (!signatureTemplate) return false
  return normalizedText === normalizeForSignatureCompare(signatureTemplate)
}

// Every unresolved INSERT* template placeholder still literally present in
// a customer-facing reply — shared between the server (send-approved-
// reply.js's send-time backstop, action-executor.js's resolveAgentSignature(),
// flow-automation.js's eligibility check — all re-exported from server/src/
// ai/template-placeholders.js, the pre-existing import path) and the UI
// (SuggestionPanel.jsx's Send-disabling draft warning), so the two can never
// disagree about what counts as an unresolved token. Matches the whole
// INSERT[A-Z0-9]+ family (INSERTEDD, INSERTDATE, INSERTADDRESS,
// INSERTORDERNUMBER, INSERTTRACKINGNUMBER, INSERTWARRANTY, INSERTLABELLINK,
// INSERTMAILINGADDRESS, INSERTRMANUMBER, and any future token following the
// same convention), with or without a leading '$' ($INSERTAMOUNT) — never a
// fixed list, so a new template's own new token is caught with no call-site
// change needed. Digits count as part of the token (2026-10-13): a
// template's INSERT5THDATE / INSERT5THBUSINESSDAY used to slip past as plain
// text and could reach a customer. Deduped, in first-seen order.
//
// UNRESOLVED_PLACEHOLDER_SOURCE is the one pattern; every other check
// (hasUnresolvedPlaceholder() below, the prompt-review export) builds on it
// rather than writing its own.
export const UNRESOLVED_PLACEHOLDER_SOURCE = String.raw`\$?INSERT[A-Z0-9]+`

// Two more placeholder styles the templates use (2026-10-21,
// docs/decisions/2026-10-21-template-tracking-fill.md), found per visible
// line (HTML is split at <br> and block ends, tags dropped):
//   - "XXX" (three or more capital X's, a word on its own) where a value
//     goes: "Estimated Delivery Date: XXX", "our XXX Warranty". "XXL" and
//     "XXXL" are words, not placeholders.
//   - A carrier pick-one list as a line's whole value: "Carrier: UPS / USPS /
//     ATS", in any order, two or more carrier names joined by "/". One
//     carrier ("Carrier: UPS") is a filled value.
// Each is reported as the line it sits on ("Estimated Delivery Date: XXX"),
// or a short snippet of a long line, so the warning names the field.
export const CARRIER_NAME_SOURCE = String.raw`(?:UPS|USPS|ATS|AMZ|Fed\s?Ex|DHL|OnTrac|LaserShip|Amazon(?:\s+(?:Transportation\s+Services|Logistics))?)`
export const CARRIER_PICK_LIST_SOURCE = String.raw`${CARRIER_NAME_SOURCE}(?:\s*\/\s*${CARRIER_NAME_SOURCE})+`
const CARRIER_PICK_LINE_RE = new RegExp(String.raw`^(?:[^:]{0,60}:\s*)?(${CARRIER_PICK_LIST_SOURCE})\s*\.?$`, 'i')
const XXX_RE = /(?<![A-Za-z0-9])X{3,}(?![A-Za-z0-9])/

// The visible lines of a reply: plain text by newline; HTML at <br>, block
// ends and newlines, tags dropped, the common entities decoded.
export function placeholderLines(text) {
  return String(text ?? '')
    .replace(/<br\s*\/?>|<\/(?:p|li|div|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
}

// Whether a visible line is a carrier pick-one line; returns the list text.
export function carrierPickList(line) {
  return CARRIER_PICK_LINE_RE.exec(String(line ?? '').trim())?.[1] ?? null
}

function placeholderSnippet(line, index) {
  if (line.length <= 80) return line
  const start = Math.max(0, line.lastIndexOf(' ', Math.max(0, index - 30)))
  const endAt = line.indexOf(' ', index + 33)
  const end = endAt === -1 ? line.length : endAt
  return `${start > 0 ? '…' : ''}${line.slice(start, end).trim()}${end < line.length ? '…' : ''}`
}

// The XXX and carrier-list placeholders, one entry per line, deduped.
export function findPatternPlaceholders(text) {
  const found = []
  for (const line of placeholderLines(text)) {
    const x = XXX_RE.exec(line)
    if (x || carrierPickList(line)) {
      const entry = x ? placeholderSnippet(line, x.index) : line
      if (!found.includes(entry)) found.push(entry)
    }
  }
  return found
}

// The INSERT* tokens first, then the XXX and carrier-list lines, then the
// RMA number's token (2026-10-27, rmartn-cust-pays.js): the Reference ID of
// an "RMARTN Cust Pays" reply, filled when the return is processed and never
// sent as text.
export function findUnresolvedPlaceholders(text) {
  const matches = String(text ?? '').match(new RegExp(UNRESOLVED_PLACEHOLDER_SOURCE, 'g')) ?? []
  return [...new Set(matches), ...findPatternPlaceholders(text), ...(String(text ?? '').includes(RMA_REFERENCE_TOKEN) ? [RMA_REFERENCE_TOKEN] : [])]
}

export function hasUnresolvedPlaceholder(text) {
  return findUnresolvedPlaceholders(text).length > 0
}

// The review reason for whatever a template fill left (2026-10-21): names the
// field the agent must fill, by its line's label ("Fill in the Estimated
// Delivery Date before sending."), the carrier list as "Choose the carrier",
// a label-less token by itself. Null when nothing is left.
// skipCarrierList: the fill already named the carrier problem.
// skipTokens: INSERT* tokens another step already owns, named without the
// "$" (Return Status, 2026-10-21: the stage choice fills INSERTDATE from the
// agent's date, and an amount the fill could not read has its own reason).
// Skipped here only; Send still refuses them while they are in the box.
export function unfilledPlaceholderReason(text, { skipCarrierList = false, skipTokens = [] } = {}) {
  const { fields, carrier } = unfilledPlaceholderFields(text, { skipTokens })
  const parts = []
  if (fields.length) parts.push(`Fill in the ${fields.join(', ')} before sending.`)
  if (carrier && !skipCarrierList) parts.push('Choose the carrier before sending.')
  return parts.length ? parts.join(' ') : null
}

// The fields a reply still has to fill (2026-10-23, the agent draft panel's
// "1 placeholder to fill: Estimated Delivery Date"): { fields, carrier }, each
// field named by its line's label when the placeholder is the line's value,
// else the token, else a snippet; carrier true when a carrier pick-one list
// is left. The same scan as unfilledPlaceholderReason().
export function unfilledPlaceholderFields(text, { skipTokens = [] } = {}) {
  const fields = []
  let carrier = false
  const add = (f) => { if (f && !fields.includes(f)) fields.push(f) }
  const skip = new Set([...(skipTokens ?? [])].map((t) => String(t).replace(/^\$/, '')))
  const tokenRe = new RegExp(UNRESOLVED_PLACEHOLDER_SOURCE, 'g')
  for (const line of placeholderLines(text)) {
    if (carrierPickList(line)) { carrier = true; continue }
    const token = (line.match(tokenRe) ?? []).find((t) => !skip.has(t.replace(/^\$/, ''))) ?? null
    const x = XXX_RE.exec(line)
    if (!token && !x) continue
    const label = /^([^:]{1,60}):/.exec(line)?.[1]?.trim() ?? null
    const at = token ? line.indexOf(token) : x.index
    // A label only when the placeholder is its value (after the colon).
    if (label && at > line.indexOf(':')) add(label)
    else if (token) add(token)
    else add(placeholderSnippet(line, x.index))
  }
  return { fields, carrier }
}

// The shadow agent's What's next lists only steps beyond the routine ones
// (2026-10-20): every contact needs the reply sent and the order note added,
// so those two say nothing and hide the steps that matter. A step that only
// says one of them is routine; one that says more ("Send the reply after
// filling INSERTCARRIERSTATUS.") is not, nor is a code-generated "Check: ..."
// item. One list for the server's filter (agent-draft.js) and the panel
// (ui/src/ShadowDraftPanel.jsx), which also hides stored rows' routine steps.
export const ROUTINE_NEXT_STEP_PATTERNS = Object.freeze([
  /^(?:send|review and send|send out) (?:the )?(?:reply|response|message)(?: to the customer)?\.?$/i,
  /^(?:add|stage|save|post) (?:the )?(?:staged )?(?:order )?note(?: to the order)?\.?$/i,
])
export function isRoutineNextStep(step) {
  const s = String(step ?? '').trim()
  if (!s || /^check:/i.test(s)) return false
  return ROUTINE_NEXT_STEP_PATTERNS.some((re) => re.test(s))
}

// Filler update lines (2026-10-21, prod contact 8224574): a customer reply
// never says that tracking or order updates will be sent by email, that
// updates will appear on their eBay order (or anywhere else), or that no
// delivery date is available. They add nothing and are often untrue. The
// prompts forbid them; this is the deterministic backstop, so a prompt
// change can't bring them back. Each pattern is sentence-level and narrow:
// a sentence that only mentions email ("Reply to this email with a photo",
// "We emailed your return label") is never matched. The key is what the
// Activity Log records, never the text.
export const FILLER_UPDATE_LINE_PATTERNS = Object.freeze([
  // "Updates for your website order will be sent by email.", "Tracking
  // updates are going to be emailed to you."
  Object.freeze({ key: 'updates_sent_by_email', re: /\bupdates?\b[^.!?;\n]{0,60}?\b(?:will|are going to|would) (?:also |continue to )?be (?:sent|e-?mailed)\b/i }),
  // "You will receive email updates.", "You'll get an email when it ships.",
  // "You will receive tracking updates by email."
  Object.freeze({ key: 'you_will_receive_email', re: /\byou(?:'ll| will) (?:also |continue to )?(?:receive|get) (?:an? )?(?:e-?mails? (?:updates?|notifications?|when|once|as soon as)\b|(?:tracking |order |shipping |delivery )?updates? (?:by|via|through) e-?mail\b)/i }),
  // "Updates will also appear on your eBay order.", "Tracking will show on the
  // eBay order."
  Object.freeze({ key: 'updates_appear_on_order', re: /\b(?:updates?|tracking(?: information| details| updates?)?|the status)\b[^.!?;\n]{0,40}?\bwill (?:also )?(?:appear|show(?: up)?|be (?:visible|posted|shown|available|updated)) (?:on|in|under) (?:your|the) (?:eBay )?(?:order(?: page| details)?|account|purchase history)\b/i }),
  // "No estimated delivery date is available.", "There is not an estimated
  // delivery date provided."
  Object.freeze({ key: 'no_delivery_date', re: /\b(?:no|not an?|(?:don't|do not|doesn't|does not) have an?)\s+(?:estimated\s+|expected\s+)?delivery date\b/i }),
])

const SENTENCE_SPLIT_RE = /(?<=[.!?])\s+/
const CLAUSE_SPLIT_RE = /(\s*;\s*|,\s+and\s+)/

function fillerKeys(text) {
  return FILLER_UPDATE_LINE_PATTERNS.filter((p) => p.re.test(text)).map((p) => p.key)
}

// The filler sentences in a text: [{ key, sentence }], in order.
export function findFillerUpdateLines(text) {
  const out = []
  for (const line of String(text ?? '').split('\n')) {
    for (const sentence of line.split(SENTENCE_SPLIT_RE)) {
      for (const key of fillerKeys(sentence)) out.push({ key, sentence: sentence.trim() })
    }
  }
  return out
}

// A clause-level removal for a note: "Advised ... and provided tracking;
// updates will be sent by email." keeps the first clause and its full stop.
function stripFillerClauses(sentence, removed) {
  const parts = sentence.split(CLAUSE_SPLIT_RE)
  if (parts.length < 3) return null
  const end = /[.!?]+$/.exec(sentence.trim())?.[0] ?? ''
  const kept = []
  for (let i = 0; i < parts.length; i += 2) {
    const clause = parts[i].replace(/[.!?]+$/, '')
    const keys = fillerKeys(clause)
    if (keys.length) { removed.push(...keys); continue }
    if (kept.length) kept.push(parts[i - 1])
    kept.push(clause)
  }
  const joined = kept.join('').trim()
  return joined ? `${joined}${end}` : ''
}

// Removes each filler sentence (clauses: true also removes a filler clause
// inside a longer sentence, for an order note). Returns { text, removed,
// emptied }: removed is the matched pattern keys, deduped; emptied is true
// when nothing but a signature line would be left, and then text is the
// input unchanged, for a person to review.
export function removeFillerUpdateLines(text, { clauses = false } = {}) {
  const input = String(text ?? '')
  const removed = []
  if (!findFillerUpdateLines(input).length) return { text, removed: [], emptied: false }
  const lines = input.split('\n').map((line) => {
    if (!fillerKeys(line).length) return line
    const kept = []
    for (const sentence of line.split(SENTENCE_SPLIT_RE)) {
      const keys = fillerKeys(sentence)
      if (!keys.length) { kept.push(sentence); continue }
      const partial = clauses ? stripFillerClauses(sentence, removed) : null
      if (partial) { kept.push(partial); continue }
      if (partial === null) removed.push(...keys)
    }
    const out = kept.join(' ').replace(/\s+$/, '')
    return out.trim() ? out : null
  })
  // A removed line goes entirely; the blank lines left around it collapse.
  const joined = lines.filter((line) => line !== null).join('\n').replace(/\n{3,}/g, '\n\n').replace(/\s+$/, '').replace(/^\s+/, '')
  const content = joined.split('\n').filter((l) => l.trim() && !l.includes('[AGENT_FIRST_NAME]'))
  const keys = [...new Set(removed)]
  if (!content.length) return { text, removed: keys, emptied: true }
  return { text: joined, removed: keys, emptied: false }
}

// Order notes record the contact, never a to-do (2026-10-22, prod contact
// 8255915): what the customer asked and what we told them. A sentence that
// tells the person what to do ("Track ... on USPS and fill in the status.")
// belongs in What's next only, and a sentence holding a placeholder
// (INSERTCARRIERSTATUS, an XXX field, a carrier pick list) records nothing
// yet. The prompts forbid both; this is the deterministic backstop, run on
// the agent's staged note and on the pipeline's note before either goes in
// the "Add note to order" step. A sentence is an instruction when it starts
// (after an optional bullet and "Please") with one of these verbs as a whole
// word, so past tense ("Confirmed shipment ...", "Called the customer") and
// a sentence about the customer ("Customer asked to check the status.")
// stay. The removed keys name the verb or the token, never the text.
export const NOTE_INSTRUCTION_VERBS = Object.freeze(['Track', 'Check', 'Verify', 'Confirm', 'Review', 'Fill in', 'Replace', 'Look up', 'Contact', 'Call', 'Ask', 'Make sure', 'Ensure'])
const NOTE_INSTRUCTION_RE = new RegExp(String.raw`^[\s\-*•]*(?:please\s+)?(${NOTE_INSTRUCTION_VERBS.map((v) => v.replace(' ', String.raw`\s+`)).join('|')})(?![\w'’-])`, 'i')

// The key of one note sentence's problem, or null: "instruction: Track" or
// "placeholder: INSERTCARRIERSTATUS" (the first token found).
function noteInstructionKey(sentence) {
  const s = String(sentence ?? '').trim()
  if (!s) return null
  const verb = NOTE_INSTRUCTION_RE.exec(s)
  if (verb) {
    const said = verb[1].replace(/\s+/g, ' ').toLowerCase()
    return `instruction: ${NOTE_INSTRUCTION_VERBS.find((v) => v.toLowerCase() === said) ?? verb[1]}`
  }
  const token = findUnresolvedPlaceholders(s)[0]
  if (token) return `placeholder: ${new RegExp(`^${UNRESOLVED_PLACEHOLDER_SOURCE}$`).test(token) ? token.replace(/^\$/, '') : 'a fill-in field'}`
  return null
}

// A sentence's clauses after a semicolon ("Customer asked for the status;
// confirm the package status on the USPS website."): an instruction or
// placeholder clause goes and the rest keeps the sentence's full stop. Null
// when the sentence has no such clause; '' when every clause is one.
function stripInstructionClauses(sentence, removed) {
  const parts = sentence.split(/\s*;\s*/)
  if (parts.length < 2) return null
  const end = /[.!?]+$/.exec(sentence.trim())?.[0] ?? ''
  const keys = []
  const kept = []
  for (const part of parts) {
    const key = noteInstructionKey(part)
    if (key) keys.push(key)
    else kept.push(part.replace(/[.!?]+$/, ''))
  }
  if (!keys.length) return null
  removed.push(...keys)
  const joined = kept.join('; ').trim()
  return joined ? `${joined}${end}` : ''
}

// Removes each instruction or placeholder sentence (or clause after a
// semicolon) from an order note. Returns { text, removed, emptied }: removed
// is the keys above, deduped, in order; emptied is true when nothing is
// left, and then text is '' (the caller writes the note in code).
export function removeNoteInstructions(note) {
  const input = String(note ?? '')
  const removed = []
  const lines = input.split('\n').map((line) => {
    const kept = []
    for (const sentence of line.split(SENTENCE_SPLIT_RE)) {
      // A sentence that starts with an instruction goes whole; otherwise
      // its clauses are checked, then the sentence for a placeholder.
      const lead = NOTE_INSTRUCTION_RE.test(sentence.trim()) ? noteInstructionKey(sentence) : null
      if (lead) { removed.push(lead); continue }
      const partial = stripInstructionClauses(sentence, removed)
      if (partial !== null) { if (partial) kept.push(partial); continue }
      const key = noteInstructionKey(sentence)
      if (key) { removed.push(key); continue }
      kept.push(sentence)
    }
    const out = kept.join(' ').replace(/\s+$/, '')
    return out.trim() ? out : (line.trim() ? null : line)
  })
  if (!removed.length) return { text: note, removed: [], emptied: false }
  const joined = lines.filter((line) => line !== null).join('\n').replace(/\n{3,}/g, '\n\n').trim()
  const keys = [...new Set(removed)]
  return joined ? { text: joined, removed: keys, emptied: false } : { text: '', removed: keys, emptied: true }
}

// "Signifyd" is internal (2026-10-19): the fraud screen that declines a
// Website order is never named to a customer. Any customer-facing send, on any
// channel, is refused while the text contains it (any case), and the reply box
// warns before Send. One helper for the UI and the server so the two can never
// disagree. "fraud" and "declined" are NOT blocked: agents may need them.
export const INTERNAL_ONLY_SEND_MESSAGE = 'the reply mentions Signifyd, which is internal only and never sent to a customer. Remove it before sending.'
export function mentionsSignifyd(text) {
  return /signifyd/i.test(String(text ?? ''))
}

// A reply that says an invoice is attached (2026-10-19, moved here
// 2026-10-23 from agent-draft.js). One helper for the UI (the reply box's
// warning and disabled Send) and the server (send-approved-reply.js's
// refusal), so the two can never disagree: an email that says an invoice is
// attached never goes out with no attachment.
export const INVOICE_CLAIM_RE = /\binvoices?\b[^.]{0,40}\battached\b|\battached\b[^.]{0,40}\binvoice/i
export const INVOICE_CLAIM_SEND_MESSAGE = 'the reply says an invoice is attached, but no invoice is attached. Attach the invoice or change the reply before sending.'
export function claimsAttachedInvoice(text) {
  return INVOICE_CLAIM_RE.test(String(text ?? ''))
}

// Every link in a reply (2026-10-15): Walmart Marketplace allows none, like
// eBay. One helper for the UI (the reply box's warning and disabled Send)
// and the server (send-approved-reply.js's refusal, flow-automation.js's
// eligibility), so the two can never disagree. A link is:
//   - an <a href> (except mailto: and tel:, which the rich editor makes of a
//     typed email address or phone number);
//   - an http:// or https:// URL;
//   - a www. address;
//   - a bare domain on a common TLD (1aauto.com, am-autoparts.com).
// An email address is never a link, nor the domain inside one
// (name@1aauto.com), nor a phone number, nor an ordinary word with a dot
// ("approx. 3", "Mr. Smith", "v2.0"). Deduped, in first-seen order.
//
// allowPlainUrls (2026-10-27, Walmart, docs/decisions/2026-10-27-walmart-
// plain-text-urls.md): a written-out http:// or https:// URL is allowed, like
// eBay's copy-and-paste link, and is not returned; so is an anchor that is
// only such a URL made clickable by the reply editor (its text is its href),
// which the send writes back as the plain URL (unlinkPlainUrlAnchors()). An
// anchor with other text, a www. address and a bare domain are still links.
const LINK_TLDS = 'com|net|org|us|co|io|inc|info|biz|edu|gov'
const TRAILING_PUNCTUATION = /[.,;:!?)\]}'"]+$/
const ANCHOR_RE = /<a\b[^>]*?\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a\s*>/gi
const decodeBasic = (v) => String(v ?? '').replace(/&amp;/gi, '&').replace(/&nbsp;/gi, ' ')
// An anchor that is only its own http(s) URL: <a href="U">U</a>.
function isPlainUrlAnchor(href, inner) {
  const h = decodeBasic(href).trim()
  return /^https?:\/\/\S+$/i.test(h) && decodeBasic(String(inner ?? '').replace(/<[^>]*>/g, '')).trim() === h
}
export function findLinks(text, { allowPlainUrls = false } = {}) {
  const found = []
  const add = (value) => {
    const v = String(value ?? '').trim().replace(TRAILING_PUNCTUATION, '')
    if (v && !found.includes(v)) found.push(v)
  }
  let s = String(text ?? '')
  if (allowPlainUrls) {
    // A plain-URL anchor counts as its written-out URL (allowed); its text
    // stays, so the URL is read once below.
    s = s.replace(ANCHOR_RE, (all, d, q, b, inner) => (isPlainUrlAnchor(d ?? q ?? b ?? '', inner) ? ` ${inner} ` : all))
  }
  for (const m of s.matchAll(/<a\b[^>]*?\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi)) {
    const href = (m[1] ?? m[2] ?? m[3] ?? '').trim()
    if (href && !/^(?:mailto|tel):/i.test(href)) add(href)
  }
  s = s.replace(/<[^>]*>/g, ' ').replace(/&amp;/gi, '&').replace(/&nbsp;/gi, ' ')
  // Email addresses first, so the domain inside one is never seen.
  s = s.replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, ' ')
  s = s.replace(/\bhttps?:\/\/[^\s<>"']+/gi, (u) => { if (!allowPlainUrls) add(u); return ' ' })
  s = s.replace(/\bwww\.[^\s<>"']+/gi, (u) => { add(u); return ' ' })
  const bareDomain = new RegExp(String.raw`(?<![\w.@-])(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:${LINK_TLDS})(?![\w-])(?:\/[^\s<>"']*)?`, 'gi')
  for (const m of s.matchAll(bareDomain)) add(m[0])
  return found
}

// A Walmart reply's links that Walmart does not allow (2026-10-27): every
// link but a written-out http(s) URL. One helper for the UI, the send and
// automation.
export function findWalmartLinks(text) {
  return findLinks(text, { allowPlainUrls: true })
}

// A plain-URL anchor (<a href="U">U</a>, the reply editor's autolink) back to
// its written-out URL (2026-10-27): a Walmart reply goes out as plain text,
// never a clickable link. Other anchors are left as they are (the send gate
// refuses them).
export function unlinkPlainUrlAnchors(html) {
  if (!html) return html ?? ''
  return String(html).replace(ANCHOR_RE, (all, d, q, b, inner) => (isPlainUrlAnchor(d ?? q ?? b ?? '', inner) ? inner : all))
}

// The one sentence the UI shows and the server refuses with.
export function walmartLinkMessage(links) {
  if (!links?.length) return null
  return `This Walmart reply contains a link (${links.join(', ')}). Walmart allows only a web address written out in full as plain text (https://...); remove the link or write it out before sending.`
}

// A Walmart Marketplace reply's signature (2026-10-15): fixed in code, no
// template and no Settings editing. [AGENT_FIRST_NAME] is resolved like every
// other signature. No links, phone numbers, hours or app promotion.
export const WALMART_SIGNATURE = '[AGENT_FIRST_NAME]\n1A Auto'

// Every proposed-action type (or type + condition) whose successful Process
// sends a message/response the customer or buyer will actually see — the
// server's routes/ai.js uses this to decide whether Processing such a step,
// on a suggestion with no draft box of its own (above), should itself count
// as the Send: decided as this agent's send and the conversation closed.
// See server/src/services/action-executor.js for the real implementations
// and docs/decisions.md (2026-09-25) for the full include/exclude audit this
// registry is built from. Deliberately excludes: every TRQ-only write (order
// notes, address/shipping-method updates, refund/replacement/return
// creation — no buyer-facing text of their own), internal ops emails (e.g.
// sales_hold_call_customer_email — a "call the customer" note to OUR team,
// never the buyer), and every eBay-only write with no free-text buyer
// message (cancellation approve/reject/create, disposition-only no-op
// branches).
const CUSTOMER_FACING_SEND_ACTION_TYPES = new Set([
  'send_ebay_member_message',
  'automated_return_notice_send_message',
  'inr_send_message',
  // The comment/content field IS buyer-facing text (eBay's own inquiry-refund
  // comment), even though the API call itself is a refund, not a plain message.
  'inr_issue_refund',
  // sellerComment is exactly the "Message to buyer (editable)" field
  // SuggestionPanel.jsx already shows for this step — buyer-facing despite
  // going through eBay's shipment-info endpoint, not a message endpoint.
  'inr_provide_shipment_info',
  'resend_return_label_send_message',
  // The double-execution incident's own root cause (docs/decisions.md,
  // 2026-09-25) — the one customer-facing, conversation-closing send action
  // that never attributed its send to a decided suggestion.
  'sales_hold_send_ebay_message',
  // A task's message on the order's open INR inquiry (2026-10-15).
  'task_case_message',
])

export function isCustomerFacingSendAction(action, rawInput) {
  if (!action?.type) return false
  if (CUSTOMER_FACING_SEND_ACTION_TYPES.has(action.type)) return true
  // Only the manual (no pre-resolved disposition) branch composes and sends
  // a real message — the already_resolved/already_resolved_confirmed
  // branches of this same action type are pure no-ops, kept only for a
  // suggestion persisted before dispositions were split into their own
  // dedicated action types.
  if (action.type === 'inr_request_id_process') return !action.params?.disposition
  return false
}
